import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  capitalize,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendContext } from "contexts/QuickSend";
import { useContext, useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { ALL } from "schemas/forms";
import { SetAlertType } from "schemas/functions";
import { routes } from "schemas/routes";

import { PAGE_VISITED } from "constants/trackingProps";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { generateColorFromString } from "utils/color";
import { sanitize } from "utils/sanitize";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";
import { removeFromList } from "utils/updateLocalState";

import DeleteTemplateDialog from "./DeleteTemplate";
import EditTemplateDialog from "./EditTemplate";
import PromoAnnualBanner from "./PromoAnnualBanner";
import styles from "./styles";

interface TemplatesTableProps {
  setAlert: SetAlertType;
}
export default function TemplatesTable({ setAlert }: TemplatesTableProps) {
  const { getToken } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const { discoverTab } = useContext(DiscoverViewContext);

  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileScreen =
    isMediumScreen || (isLargeScreen && discoverTab !== null);

  const { setErrorAlert } = useContext(AlertContext);

  const { currentOrg, setEmailSettings } = useContext(OrganizationUserContext);
  const {
    fetchOutreachTemplates,
    templates,
    setTemplates,
    fetchOutreachTemplatesLoading,
    outreachTemplates,
    setCreateTemplateDialogOpen,
  } = useContext(OutreachTemplatesContext);
  const { setNewlyAddedTemplateIndex } = useContext(QuickSendContext);
  const { setCreateTemplateOnboardingDialogOpen } = useContext(
    OnboardingDialogsContext,
  );

  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null,
  );
  const [deleteTemplateLoading, setDeleteTemplateLoading] = useState(false);
  const [fetchTemplateLoading, setFetchTemplateLoading] = useState<
    number | null
  >(null);
  const [filter, setFilter] = useState(ALL);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTemplate, setMenuTemplate] = useState<Template | null>(null);
  const [emailCounts, setEmailCounts] = useState<{ [key: number]: number }>({});

  useEffect(() => {
    if (currentOrg?.id) {
      // If Organization is VA user, we want to fetch niches
      // along with templates.
      fetchOutreachTemplates();
      fetchEmailCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    trackEvent(PAGE_VISITED);
  }, []);

  const noTemplatesFound =
    outreachTemplates?.length === 0 && !fetchOutreachTemplatesLoading;
  useEffect(() => {
    if (noTemplatesFound) {
      trackCreateTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noTemplatesFound]);

  const getFilteredTemplates = () => {
    let templatesWithIds = templates?.filter((x) => x.id);
    if (
      filter === TemplateContentType.OUTREACH ||
      filter === TemplateContentType.FOLLOWUP
    ) {
      templatesWithIds = templatesWithIds?.filter(
        (x) => x.contentType === filter,
      );
    }
    return templatesWithIds;
  };

  const handleOpenDeleteTemplate = async (template: Template) => {
    if (!currentOrg?.id) {
      return;
    }
    if (
      outreachTemplates?.length <= 1 &&
      template?.contentType === TemplateContentType.OUTREACH
    ) {
      setAlert("Need at least one outreach template", "warning");
      return;
    }
    trackEvent("Delete Template Button Clicked", {
      "Template ID": template.id,
    });
    if (template?.id) setFetchTemplateLoading(template?.id);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${template.id}`,
      );
      setTemplateToDelete(res.template);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchTemplateLoading(null);
    }
  };

  const handleDeleteTemplate = async (replacedTemplateId: number | null) => {
    setDeleteTemplateLoading(true);
    try {
      const { emailSetting } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${templateToDelete?.id}`,
        "DELETE",
        {},
        {
          replacedTemplateId,
        },
      );
      if (emailSetting) setEmailSettings(emailSetting);
      removeFromList(templates, setTemplates, templateToDelete, "id");
      fetchOutreachTemplates();
      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setDeleteTemplateLoading(false);
    }
  };

  const handleClose = () => {
    setTemplateToDelete(null);
  };

  const trackCreateTemplate = () => {
    trackEvent("Add Template Button Clicked", {
      "Clicked From": "Templates Table View",
    });

    if (outreachTemplates?.length === 0) {
      setCreateTemplateOnboardingDialogOpen(true);
      return;
    }
    setCreateTemplateDialogOpen(true);
    setNewlyAddedTemplateIndex(-1);
  };

  const handleCopyTemplate = async (template: Template) => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates`,
        "POST",
        {},
        {
          name: `${template?.name} - Copy`,
          subject: template?.subject,
          body: template?.body,
          contentType: template?.contentType,
          attachment: template?.attachment,
        },
      );
      setTemplates((prev) => [...prev, res.template]);
      navigate(`/${routes.templates}/${res.template.id}`);
      setAlert("Successfully duplicated template", "success");
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleTemplateClick = (template: Template) => {
    navigate(`/${routes.templates}/${template.id}`);
  };

  const onTemplateSuccess = () => {
    navigate(`/${routes.templates}`, { replace: true });
  };

  const fetchEmailCount = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/scheduled-email-counts`,
        "GET",
      );
      setEmailCounts(res.scheduledEmailCounts);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  return (
    <Box sx={styles.root}>
      <PromoAnnualBanner />

      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 3 }}
      >
        <Box>
          <Typography variant="h3" gutterBottom>
            Your Templates
          </Typography>
          <Typography sx={{ fontSize: 18 }}>
            Create different templates for different niches to get better
            replies
          </Typography>

          <Grid container sx={styles.filterContainer}>
            <Button
              sx={filter === ALL ? styles.filterActive : styles.filterInactive}
              onClick={() => setFilter(ALL)}
            >
              Show All
            </Button>
            <Button
              sx={
                filter === TemplateContentType.OUTREACH
                  ? styles.filterActive
                  : styles.filterInactive
              }
              onClick={() => setFilter(TemplateContentType.OUTREACH)}
            >
              Outreach
            </Button>
            <Button
              sx={
                filter === TemplateContentType.FOLLOWUP
                  ? styles.filterActive
                  : styles.filterInactive
              }
              onClick={() => setFilter(TemplateContentType.FOLLOWUP)}
            >
              Follow-up
            </Button>
          </Grid>
        </Box>
        <Grid
          item
          xs={12}
          md="auto"
          container
          gap={1}
          justifyContent={isMobileScreen ? "flex-start" : "flex-end"}
        >
          <Button
            variant="contained"
            disableElevation
            onClick={trackCreateTemplate}
            sx={{ px: 3, py: 1 }}
          >
            + Create A Template
          </Button>
        </Grid>
      </Grid>

      {fetchOutreachTemplatesLoading ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={2}>
          {getFilteredTemplates()?.map((template: Template) => (
            <Grid item xs={12} sm={6} lg={4} key={template.id}>
              <Card
                sx={styles.card}
                onClick={() => handleTemplateClick(template)}
              >
                <CardContent sx={styles.cardContent}>
                  <Box sx={styles.cardHeader}>
                    <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                      {template?.niches && template?.niches?.length > 0 && (
                        <Tooltip
                          enterTouchDelay={0}
                          title={template?.niches
                            ?.map((niche) => niche.name)
                            .join(", ")}
                        >
                          <Box display="flex" flexWrap="wrap">
                            {template?.niches &&
                            template?.niches?.length > 0 ? (
                              <Chip
                                key={template?.niches?.[0]?.id}
                                label={<>{template?.niches?.[0]?.name}</>}
                                sx={[
                                  styles.nichesChip,
                                  {
                                    zIndex: 2,
                                    borderRight: "3px solid white",
                                    backgroundColor: generateColorFromString(
                                      template?.niches?.[0]?.name,
                                      "tagColorPalette",
                                    ),
                                  },
                                ]}
                              />
                            ) : (
                              <></>
                            )}
                            {template?.niches?.length &&
                            template?.niches?.length > 1 ? (
                              <Chip
                                label={<>+{template?.niches?.length - 1}</>}
                                sx={styles.moreChip}
                              />
                            ) : (
                              <></>
                            )}
                          </Box>
                        </Tooltip>
                      )}

                      <Chip
                        label={
                          <>
                            {template?.contentType ===
                            TemplateContentType.OUTREACH ? (
                              <Box
                                component="i"
                                className="fa-regular fa-envelope"
                              />
                            ) : (
                              <Box
                                component="i"
                                className="fa-regular fa-reply"
                              />
                            )}{" "}
                            {capitalize(template?.contentType)}
                          </>
                        }
                        sx={styles.contentTypeChip(template?.contentType)}
                      />
                    </Box>

                    <IconButton
                      sx={styles.menuButton}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuAnchorEl(e.currentTarget);
                        setMenuTemplate(template);
                      }}
                    >
                      <Box
                        component="i"
                        className="fa-solid fa-ellipsis"
                        sx={{ color: grey[300] }}
                      />
                    </IconButton>
                  </Box>
                  <Typography sx={styles.templateName}>
                    {template?.name}
                  </Typography>

                  <Typography>
                    {template?.subject && (
                      <Box
                        dangerouslySetInnerHTML={{
                          __html: `Subject: ${sanitize(template?.subject)}`,
                        }}
                        sx={{ color: grey[500] }}
                      />
                    )}
                  </Typography>

                  {template?.body && (
                    <Box
                      dangerouslySetInnerHTML={{
                        __html:
                          template?.body?.length > 400
                            ? `${sanitize(template?.body)?.slice(0, 400)}...`
                            : sanitize(template?.body),
                      }}
                    />
                  )}
                  <Divider sx={{ my: 2, backgroundColor: "purple.light" }} />

                  <Grid container alignItems="center" gap={1}>
                    {template?.id &&
                      template?.id in emailCounts &&
                      emailCounts[template?.id] > 0 && (
                        <Tooltip
                          title={`There are ${emailCounts[template?.id!]} scheduled email(s) using this template.`}
                        >
                          <Typography sx={styles.lastModified}>
                            <Box
                              component="i"
                              className="fa-regular fa-envelope"
                            />{" "}
                            {emailCounts[template?.id!]} in use
                          </Typography>
                        </Tooltip>
                      )}
                    {template?.updatedAt && (
                      <Typography sx={styles.lastModified}>
                        Last modified on{" "}
                        {template?.updatedAt
                          ? formatEpochTimestamp({
                              epochTimestamp: template?.updatedAt,
                              addYear: true,
                            })
                          : "--"}
                      </Typography>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null);
          setMenuTemplate(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuTemplate) {
              trackEvent("Template Edited", {
                "Template ID": menuTemplate.id,
              });
              handleTemplateClick(menuTemplate);
            }
            setMenuAnchorEl(null);
            setMenuTemplate(null);
          }}
        >
          <ListItemIcon>
            <Box component="i" className="fa-regular fa-pen" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuTemplate) {
              trackEvent("Template Duplicated", {
                "Template ID": menuTemplate.id,
              });
              handleCopyTemplate(menuTemplate);
            }
            setMenuAnchorEl(null);
            setMenuTemplate(null);
          }}
        >
          <ListItemIcon>
            <Box component="i" className="fa-regular fa-clipboard" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (menuTemplate) {
              handleOpenDeleteTemplate(menuTemplate);
            }
            setMenuAnchorEl(null);
            setMenuTemplate(null);
          }}
          disabled={fetchTemplateLoading === menuTemplate?.id}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            {fetchTemplateLoading === menuTemplate?.id ? (
              <CircularProgress size={16} />
            ) : (
              <Box component="i" className="fa-regular fa-trash-can" />
            )}
          </ListItemIcon>
          <ListItemText
            sx={{
              "& .MuiTypography-root": {
                color: "error.main",
              },
            }}
          >
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      <Routes>
        <Route
          path=":templateId"
          element={<EditTemplateDialog onSuccess={onTemplateSuccess} />}
        />
      </Routes>
      {templateToDelete && (
        <DeleteTemplateDialog
          open={Boolean(templateToDelete?.id)}
          handleClose={() => setTemplateToDelete(null)}
          template={templateToDelete}
          handleConfirm={(replacedTemplateId: number | null) => {
            handleDeleteTemplate(replacedTemplateId);
          }}
          loading={deleteTemplateLoading}
        />
      )}
    </Box>
  );
}
